import { useState } from "react";
import { Mail, Lock, ArrowRight, AlertCircle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { KMark } from "../components/KMark";

export default function LoginPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phase, setPhase] = useState<"idle" | "submitting">("idle");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setPhase("submitting");
    setError(null);

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (signInError) {
      setPhase("idle");
      // Map common Supabase errors to nicer FR text
      const msg = signInError.message.toLowerCase();
      if (msg.includes("invalid login credentials")) {
        setError("Email ou mot de passe incorrect.");
      } else if (msg.includes("email not confirmed")) {
        setError("Email pas encore confirmé. Vérifie tes courriels.");
      } else {
        setError(signInError.message);
      }
    }
    // On success, useAuth listens to auth state change and re-renders.
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container max-w-lg pt-20 md:pt-32 pb-10">
        <div className="mb-12 flex items-center gap-3">
          <div
            className="grid place-items-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: "#fafafa",
              color: "#0a0a0a",
              boxShadow: "0 1px 2px rgba(0,0,0,0.4), inset 0 0.5px 0 rgba(255,255,255,0.6)",
            }}
          >
            <KMark className="h-[22px] w-auto" />
          </div>
          <div>
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase font-medium">Koloc Tools</div>
            <div className="font-mono text-[9px] tracking-[0.18em] uppercase text-foreground/40 mt-0.5">Outils internes</div>
          </div>
        </div>

        <div className="mb-2 label-caps">Connexion</div>
        <h1 className="title-dual text-h1">
          Tes outils <span className="dim">internes Koloc</span>
        </h1>
        <p className="mt-4 text-foreground/60 text-lead">
          Email + mot de passe. Accès sur invitation seulement.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <div>
            <label htmlFor="email" className="label-caps mb-2 block">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40 pointer-events-none" />
              <input
                id="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="prenom@koloc.studio"
                className="input-koloc pl-11"
                disabled={phase === "submitting"}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="label-caps mb-2 block">
              Mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40 pointer-events-none" />
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input-koloc pl-11"
                disabled={phase === "submitting"}
              />
            </div>
          </div>

          <button type="submit" disabled={phase === "submitting" || !email || !password} className="pill-solid w-full">
            {phase === "submitting" ? "Connexion…" : "Se connecter"}
            {phase !== "submitting" && <ArrowRight className="h-4 w-4" />}
          </button>

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-caption text-destructive flex gap-3">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}
        </form>

        <div className="mt-10 divider" />
        <p className="mt-6 text-caption text-foreground/40">
          Pas de compte ? Demande à un admin de Koloc de t'ajouter dans Settings → Utilisateurs.
        </p>
        <p className="mt-2 text-caption text-foreground/40">
          Mot de passe oublié ? Demande à un admin de te renvoyer un lien de réinitialisation depuis le dashboard Supabase.
        </p>
      </div>
    </div>
  );
}
